import { Fragment, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router';
import { fetchMedicalHome, updateNewAnswer } from '@/api';
import ReceiptLayout from '@/components/MedicalReceipt/ReceiptLayout';
import TopSection from '@/components/MedicalHome/TopSection';
import BottomSection from '@/components/MedicalHome/BottomSection';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { IGlobalState } from '@/store';
import {
  IMedicalHomePayload,
  IMedicalHomeResponse,
} from '@/types/medical.type';
import LoadingView from '@/components/common/LoadingView';
import DepartureRefundInfoModal from '@/components/MedicalHome/DepartureModal/DepartureRefundInfo.modal';
import { subDays } from 'date-fns';
import { AnswerdModal } from '@repo/components';
import { routes } from '@/constant/routes.const';
import { MTCN } from '@repo/types';

function MedicalHome() {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const {
    passportNumber,
    hasNewAnswer,
    isRefundInfoRegistered,
    departureDate,
    nationality,
  } = useAppSelector((state) => state) as IGlobalState;
  const LANG = nationality === 'KOR' ? 'KOR' : 'ENG';
  const [isOpenRefundInfoModal, setIsOpenRefundInfoModal] = useState(false);
  const [mtcnList, setMtcnList] = useState<MTCN[]>([]);

  const onCloseModal = () => {
    dispatch({
      type: 'UPDATE_HOME_MODAL_STATE',
      payload: { isActive: false, data: null, type: null },
    });
  };
  //@ts-ignore
  const { mutate: homeMutation, isLoading } = useMutation<
    IMedicalHomeResponse,
    AxiosError,
    IMedicalHomePayload
  >((payload) => fetchMedicalHome(payload), {
    retry: false,
    onSuccess: ({
      isRegister,
      isRead,
      paymentStatus,
      isVincentJpn,
      ...rest
    }) => {
      dispatch({
        type: 'UPDATE_USER_INFO',
        payload: {
          isRefundInfoRegistered: isRegister,
          haveSeenReceipt: isRead,
          paymentStatus,
          ...rest,
        },
      });
      setMtcnList(rest.mtcnInfo?.mtcnList || []);
    },
  });

  const fetchHome = () => {
    homeMutation({ passportNumber: passportNumber as string });
  };

  useEffect(() => {
    fetchHome();
  }, [passportNumber]);

  useEffect(() => {
    const preventGoBack = () => {
      onCloseModal();
      history.go(1);
    };
    window.addEventListener('popstate', preventGoBack);
    return () => window.removeEventListener('popstate', preventGoBack);
  }, []);

  useEffect(() => {
    if (departureDate && nationality === 'KOR') {
      const departureYesterday = subDays(new Date(departureDate), 1);
      const isPastOrToday =
        departureYesterday.getTime() <= new Date().setHours(0, 0, 0, 0);
      setIsOpenRefundInfoModal(isPastOrToday && !isRefundInfoRegistered);
    }
  }, [isRefundInfoRegistered, departureDate]);

  const { mutate: readAnswer } = useMutation(
    () => updateNewAnswer(passportNumber || ''),
    {
      onSuccess: () => {
        sessionStorage.setItem('inquiry-endpoint', routes.medicalHome);
        history.push(routes.inquiryQna);
      },
      onError: (error: any) => {
        alert(error.message);
      },
    },
  );

  return (
    <ReceiptLayout hasPadding={false}>
      {isLoading ? (
        <LoadingView />
      ) : (
        <Fragment>
          <TopSection mtcnList={mtcnList} />
          <BottomSection />
        </Fragment>
      )}
      <AnswerdModal isOpen={hasNewAnswer} onClick={readAnswer} lang={LANG} />
      {nationality === 'KOR' && isOpenRefundInfoModal && (
        <DepartureRefundInfoModal
          onClose={() => setIsOpenRefundInfoModal(false)}
        />
      )}
    </ReceiptLayout>
  );
}
export default MedicalHome;
